import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://bookoll.com';
const OG_IMAGE = `${SITE_URL}/og-home.jpg`;

// respuestaGratis se recibe como prop desde FAQ.tsx, ya resuelta con la
// promo activa real (vista_promocion_registro_activa), para que el JSON-LD
// que leen los buscadores nunca quede con una fecha caducada hardcodeada.
function buildFaqSchemaItems(respuestaGratis: string) {
  return [
    {
      pregunta: '¿Qué es Bookoll?',
      respuesta:
        'Bookoll es una plataforma digital para profesionales, autónomos y pequeñas empresas que necesitan gestionar reservas online, citas, clientes y trabajadores desde un único panel. Además de sistema de reservas, ofrece un perfil profesional online que actúa como escaparate digital del negocio.',
    },
    {
      pregunta: '¿Para quién está pensado Bookoll?',
      respuesta:
        'Bookoll está pensado para profesionales, autónomos y pymes que trabajan con citas previas: peluquerías, clínicas, entrenadores personales, fisioterapeutas, psicólogos, esteticistas, nutricionistas y cualquier negocio de servicios que necesite gestionar su agenda online.',
    },
    {
      pregunta: '¿Qué tipos de usuario existen en Bookoll?',
      respuesta:
        'Existen tres tipos de usuario: Empresa (administra el perfil, servicios, trabajadores y citas), Trabajador (consulta su agenda y gestiona sus citas) y Cliente (reserva citas y gestiona su historial desde su panel personal).',
    },
    {
      pregunta: '¿Cuánto se tarda en empezar a usar Bookoll?',
      respuesta:
        'Muy poco. En solo unos minutos puedes crear tu cuenta, configurar tus servicios y definir tu disponibilidad. Una vez hecho esto tendrás tu agenda lista para recibir reservas y tu perfil profesional visible para los clientes.',
    },
    {
      pregunta: '¿Cómo registro mi negocio en Bookoll?',
      respuesta:
        'El registro tarda menos de 5 minutos y no requiere tarjeta de crédito. Solo necesitas crear tu cuenta con email y contraseña (o Google), añadir la información básica del negocio (nombre, categoría, descripción), indicar la ubicación y el teléfono, y aceptar las condiciones. Tras eso tendrás acceso inmediato a tu panel de empresa.',
    },
    {
      pregunta: '¿Puedo activar o desactivar las reservas online en Bookoll?',
      respuesta:
        'Sí. Desde la configuración de empresa puedes activar o desactivar las reservas online en cualquier momento. También puedes restringir las reservas para que solo las realicen clientes registrados en Bookoll.',
    },
    {
      pregunta: '¿Qué son los bonos en Bookoll?',
      respuesta:
        'Los bonos son paquetes de sesiones de un servicio concreto que se venden a un precio cerrado. Cada bono tiene una cantidad de usos, un precio total y una vigencia en días. Los clientes pueden comprar bonos y consumirlos en futuras citas.',
    },
    {
      pregunta: '¿Qué notificaciones automáticas envía Bookoll?',
      respuesta:
        'Bookoll envía confirmación de cita al cliente, trabajador y gestor; recordatorio configurable entre 1 y 72 horas antes por email; y notificación de cancelación o reprogramación. Cada canal se puede activar o desactivar de forma independiente.',
    },
    {
      pregunta:
        '¿Cuántos días de antelación se muestran en el calendario de reservas?',
      respuesta:
        'Es configurable por empresa entre 1 y 60 días. El valor por defecto es 14 días. Los clientes solo ven los días con disponibilidad real según el horario y las citas ya existentes.',
    },
    {
      pregunta: '¿Cómo accedo al panel de trabajador en Bookoll?',
      respuesta:
        'Una vez que la empresa te envía el código de invitación y completas el registro, accedes desde la pantalla de perfiles a tu panel de trabajador. Desde ahí puedes ver tu agenda, citas del día, citas pendientes y tu perfil personal.',
    },
    {
      pregunta: '¿Para qué sirve el código QR del trabajador en Bookoll?',
      respuesta:
        'Cada trabajador puede generar y mostrar un QR desde su panel. Al escanearlo, el cliente accede directamente a la página de reservas de la empresa, facilitando la captación en el punto de venta físico.',
    },
    {
      pregunta: '¿Necesito una cuenta para reservar una cita en Bookoll?',
      respuesta:
        'Depende de la configuración del negocio. Algunos permiten reservar sin registro introduciendo nombre, email y teléfono. Otros requieren una cuenta activa en Bookoll. En cualquier caso, registrarse es gratuito y permite cancelar citas, ver el historial y gestionar el perfil.',
    },
    {
      pregunta: '¿Cómo cancelo una cita en Bookoll?',
      respuesta:
        'Si tienes cuenta en Bookoll, accede a Panel Cliente → Mis citas y cancela la cita deseada. Si reservaste sin cuenta, contacta directamente con el negocio para solicitar la cancelación.',
    },
    {
      pregunta: '¿Puedo valorar una cita completada en Bookoll?',
      respuesta:
        'Sí. Después de que una cita quede marcada como completada, puedes dejar una puntuación de 0 a 5 y un comentario opcional. Las valoraciones son visibles en el perfil público de la empresa.',
    },
    {
      pregunta: '¿Puedo usar Bookoll desde el móvil?',
      respuesta:
        'Sí, Bookoll funciona desde móvil, tablet y ordenador, directamente desde el navegador sin necesidad de instalar ninguna aplicación.',
    },
    {
      pregunta:
        '¿Los datos de clientes son compartidos entre empresas en Bookoll?',
      respuesta:
        'No. Cada empresa solo tiene acceso a los datos de los clientes que han reservado en ella. Los datos no se comparten ni son visibles para otras empresas de la plataforma.',
    },
    {
      pregunta: '¿Puedo solicitar la eliminación de mis datos en Bookoll?',
      respuesta:
        'Sí. Puedes solicitar la eliminación de tu cuenta y todos los datos asociados enviando un email a privacidad@bookoll.com. Procesaremos la solicitud en un plazo máximo de 30 días conforme al RGPD.',
    },
    {
      pregunta: '¿Cómo contacto con el soporte de Bookoll?',
      respuesta:
        'Escríbenos a soporte@bookoll.com. Respondemos en menos de 24 horas en días laborables.',
    },
    {
      pregunta: '¿Es gratuito usar Bookoll?',
      respuesta: respuestaGratis,
    },
  ];
}

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Inicio', item: SITE_URL },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Preguntas frecuentes',
      item: `${SITE_URL}/faq`,
    },
  ],
};

interface FaqSEOProps {
  // Respuesta ya formateada con la promo activa real (o null si no hay
  // ninguna activa ahora mismo; en ese caso se usa un texto neutro).
  respuestaGratis?: string | null;
}

export default function FaqSEO({ respuestaGratis }: FaqSEOProps) {
  const title = 'Preguntas frecuentes — Bookoll | Software de reservas online';
  const description =
    'Resuelve tus dudas sobre Bookoll: cómo registrar tu negocio, gestionar citas, configurar servicios, trabajadores, bonos, privacidad y soporte técnico. Más de 30 respuestas.';
  const canonical = `${SITE_URL}/faq`;

  const faqSchemaItems = buildFaqSchemaItems(
    respuestaGratis ??
      'Bookoll siempre tiene un plan gratuito disponible para empezar. Consulta los planes de pago en la página de precios.'
  );

  const faqPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqSchemaItems.map(({ pregunta, respuesta }) => ({
      '@type': 'Question',
      name: pregunta,
      acceptedAnswer: {
        '@type': 'Answer',
        text: respuesta,
      },
    })),
  };

  return (
    <Helmet>
      {/* ── Primary ────────────────────────────────────────────────────── */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta
        name="keywords"
        content="preguntas frecuentes bookoll, faq reservas online, cómo reservar cita online, gestión agenda negocios, software reservas preguntas, dudas bookoll, ayuda bookoll"
      />
      <meta
        name="robots"
        content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
      />
      <link rel="canonical" href={canonical} />
      {/* FIX: hreflang añadido — antes no existía */}
      <link rel="alternate" hrefLang="es-ES" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* ── Open Graph ─────────────────────────────────────────────────── */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Bookoll" />
      <meta property="og:locale" content="es_ES" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      {/* FIX: cambiado de /og-faq.jpg (no existe) a /og-home.jpg */}
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta
        property="og:image:alt"
        content="Preguntas frecuentes de Bookoll — Software de reservas online"
      />

      {/* ── Twitter Card ───────────────────────────────────────────────── */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@bookoll" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={OG_IMAGE} />
      <meta
        name="twitter:image:alt"
        content="FAQ Bookoll — Software de reservas online"
      />

      {/* ── JSON-LD ────────────────────────────────────────────────────── */}
      <script type="application/ld+json">
        {JSON.stringify(faqPageSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
    </Helmet>
  );
}